import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { AppPaths } from "@p0security/shared/routes/constants";
import { Button, Flex, List, Modal, Tooltip, Typography, message } from "antd";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { Tenant } from "components/Login";
import { AuthzButton } from "components/common/AuthzButton";
import { ConstantWidthDiv } from "components/divs";
import { doc, writeBatch } from "firebase/firestore";
import { orderBy } from "lodash";
import { DB } from "providers/FirestoreProvider";
import { useCallback, useContext, useMemo, useState } from "react";
import { StyledTag } from "styles/StyledTags";
import { colors } from "styles/variables";

import {
  FindingsContext,
  MonitorWithCount,
  MonitorWithMetadata,
} from "../contexts/FindingsContext";
import { useNavigateWithEnv } from "../hooks/useNavigateWithEnv";
import { EditMonitorModal } from "./EditMonitorModal";
import { LinkWithEnvironment } from "./LinkWithEnvironment";
import { MonitorPriority } from "./monitor/MonitorSeverity";

const MonitorActionButton: React.FC<{
  actionType: "archive" | "edit" | "template" | "unarchive" | "view";
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
}> = ({ actionType, icon, tooltip, onClick }) => {
  return (
    <Tooltip title={tooltip}>
      <AuthzButton
        roles={actionType === "view" ? "all" : ["owner", "iamOwner"]}
        type="text"
        size="small"
        aria-label={tooltip}
        icon={icon}
        onClick={onClick}
      />
    </Tooltip>
  );
};

export const MonitorList: React.FC<{
  actionTypes: string[];
  showFindings?: boolean;
  showArchived?: boolean;
}> = ({ actionTypes, showFindings = true, showArchived = false }) => {
  const { details } = useContext(SelectedEnvironmentContext);
  const assessmentId = details?.assessmentId;
  const tenantId = useContext(Tenant);
  const navigate = useNavigateWithEnv();
  const { prioritized, findingParams, archived, loading } =
    useContext(FindingsContext);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [modalMonitor, setModalMonitor] = useState<MonitorWithMetadata>();
  const [archiveModalType, setArchiveModalType] = useState<
    "archive" | "unarchive"
  >("archive");

  const filteredMonitors = useMemo(() => {
    const output = showArchived ? archived : prioritized;

    const zeroesAtEnd = orderBy(output, [(item) => item.count > 0], ["desc"]);

    return zeroesAtEnd;
  }, [prioritized, showArchived, archived]);

  const monitorPath = useCallback(
    (item: MonitorWithMetadata) =>
      `${AppPaths.Posture}/monitors/${item.monitorId}?${findingParams}`,
    [findingParams]
  );

  const actionCallbacks = useMemo(
    () => ({
      view: (item: MonitorWithMetadata) =>
        navigate(monitorPath(item), {}, true),
      edit: (item: MonitorWithMetadata) => {
        setModalMonitor(item);
        setEditModalOpen(true);
      },
      template: (item: MonitorWithMetadata) => {
        const params = new URLSearchParams({
          templateId: item.monitorId,
          show: item.show,
        });
        navigate(`${AppPaths.Inventory}?${params.toString()}`, {}, true);
      },
      archive: (item: MonitorWithMetadata) => {
        setModalMonitor(item);
        setArchiveModalType("archive");
        setArchiveModalOpen(true);
      },
      unarchive: (item: MonitorWithMetadata) => {
        setModalMonitor(item);
        setArchiveModalType("unarchive");
        setArchiveModalOpen(true);
      },
    }),
    [navigate, monitorPath]
  );
  const handleClick = useCallback(
    (effectiveActionType: string, item: MonitorWithMetadata) => {
      return () => {
        actionCallbacks[
          effectiveActionType as
            | "archive"
            | "edit"
            | "template"
            | "unarchive"
            | "view"
        ](item);
      };
    },
    [actionCallbacks]
  );

  const actions = useMemo(
    () => (item: MonitorWithMetadata) => {
      const iconMap = {
        view: <EyeOutlined />,
        edit: <EditOutlined />,
        archive: <DeleteOutlined />,
        unarchive: <UndoOutlined />,
        template: <CopyOutlined />,
      };

      const tooltipMap = {
        view: "View",
        edit: "Edit",
        archive: "Archive",
        unarchive: "Unarchive",
        template: "Use as Template",
      };

      return actionTypes.map((actionType) => {
        if (actionType === "template" && item?.isCustom) {
          // Render invisible placeholder button to maintain consistent spacing
          return (
            <Tooltip key={actionType} title="">
              <Button
                type="text"
                size="small"
                icon={<span />}
                style={{ visibility: "hidden", pointerEvents: "none" }}
              />
            </Tooltip>
          );
        }

        // Use unarchive action type for archived monitors
        const effectiveActionType =
          actionType === "archive" && item?.archived ? "unarchive" : actionType;

        return (
          <MonitorActionButton
            key={actionType}
            actionType={
              effectiveActionType as
                | "archive"
                | "edit"
                | "template"
                | "unarchive"
                | "view"
            }
            icon={iconMap[effectiveActionType as keyof typeof iconMap]}
            tooltip={tooltipMap[effectiveActionType as keyof typeof tooltipMap]}
            onClick={handleClick(effectiveActionType, item)}
          />
        );
      });
    },
    [actionTypes, handleClick]
  );

  const archiveMonitor = useCallback(
    async (monitor: MonitorWithMetadata, unarchive = false) => {
      setArchiveLoading(true);
      try {
        const batch = writeBatch(DB);
        const assessmentPath = toAssessmentPath(tenantId, assessmentId ?? "");
        const monitorDocPath = `${assessmentPath}/monitors/${encodeURI(monitor.monitorId)}`;
        const isPreset = !monitor?.isCustom;

        if (isPreset) {
          // for preset monitors, the document may not exist yet
          batch.set(
            doc(DB, monitorDocPath),
            { archived: !unarchive },
            { merge: true }
          );
        } else {
          batch.update(doc(DB, monitorDocPath), {
            archived: !unarchive,
          });
        }

        await batch.commit();
        message.success(
          `Successfully ${unarchive ? "unarchived" : "archived"} monitor`
        );
      } catch (error) {
        message.error(
          `Failed to ${unarchive ? "unarchive" : "archive"} monitor: ${error}`
        );
      } finally {
        setArchiveLoading(false);
      }
    },
    [assessmentId, tenantId]
  );

  const renderMonitor = useCallback(
    (item: MonitorWithCount) => {
      return (
        <List.Item actions={actions(item)}>
          <List.Item.Meta
            title={
              <Flex>
                <LinkWithEnvironment
                  to={monitorPath(item)}
                  withTenant={true}
                  style={{ color: colors.primary["50"] }}
                >
                  {item.label}
                </LinkWithEnvironment>
              </Flex>
            }
            description={
              <Flex align="start" style={{ marginRight: "1em" }}>
                {item.isCustom && (
                  <StyledTag color={colors.tagColors.grey} bordered={false}>
                    Custom
                  </StyledTag>
                )}
                <Typography.Paragraph
                  style={{
                    width: "90%",
                    color: colors.neutral["45"],
                    // Need to set this as only paragraphs have the ellipsis prop
                    marginBottom: "0",
                  }}
                  ellipsis={{
                    rows: 1,
                    tooltip: true,
                  }}
                >
                  {item.description}
                </Typography.Paragraph>
              </Flex>
            }
          />
          <ConstantWidthDiv width="10em">
            <Flex>
              <MonitorPriority monitor={item} />
            </Flex>
          </ConstantWidthDiv>
          {showFindings && (
            <ConstantWidthDiv width="5em">
              <StyledTag
                color={
                  item.count === 0
                    ? colors.tagColors.blue
                    : colors.tagColors.lightGrey
                }
                style={{
                  textAlign: "center",
                }}
              >
                {item.count}
              </StyledTag>
            </ConstantWidthDiv>
          )}
        </List.Item>
      );
    },
    [actions, monitorPath, showFindings]
  );

  const closeEditModal = useCallback(() => setEditModalOpen(false), []);
  const closeArchiveModal = useCallback(() => setArchiveModalOpen(false), []);
  const handleUnarchive = useCallback(() => {
    if (!modalMonitor) return;
    archiveMonitor(modalMonitor, archiveModalType === "unarchive");
    setArchiveModalOpen(false);
  }, [archiveModalType, archiveMonitor, modalMonitor]);

  return (
    <>
      <List
        loading={loading}
        dataSource={filteredMonitors}
        renderItem={renderMonitor}
      />
      {actionTypes.includes("edit") && (
        <EditMonitorModal
          editModalOpen={editModalOpen}
          closeEditModal={closeEditModal}
          modalMonitor={modalMonitor}
          isCustom={modalMonitor?.isCustom ?? false}
        />
      )}
      {(actionTypes.includes("archive") ||
        actionTypes.includes("unarchive")) && (
        <Modal
          open={archiveModalOpen}
          footer={false}
          onCancel={closeArchiveModal}
        >
          <Typography.Title level={4}>Are you sure?</Typography.Title>
          {archiveModalType === "archive" ? (
            <Typography.Paragraph>
              This will archive this monitor and remove it from future
              assessments. You can unarchive this at a later date.
            </Typography.Paragraph>
          ) : (
            <Typography.Paragraph>
              This will re-activate this monitor and add it to future
              assessments.
            </Typography.Paragraph>
          )}
          <Typography.Paragraph code>
            {modalMonitor?.label}
          </Typography.Paragraph>
          <Button
            type="primary"
            danger={archiveModalType === "archive"}
            loading={archiveLoading}
            onClick={handleUnarchive}
          >
            {archiveModalType === "archive" ? "Archive" : "Re-activate"}
          </Button>
          <Button type="text" onClick={closeArchiveModal}>
            Cancel
          </Button>
        </Modal>
      )}
    </>
  );
};
